import {
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { flatten, union } from 'lodash-es';

const groupType = '-group';
const itemType = '-item';

export class TemplateLevelsChildrenHelper {
    public getRelationsAmongLevelsOfTemplate(template: IAccountMappingTemplate): {
        groupsLevelIdToLevelChildrenId: Map<string, string[]>;
        groupsLevelsIdToParentsLevelsId: Map<string, Set<string>>;
    } {
        let templateLevelsIdToLevelsChildrenId = {
            groupsLevelIdToLevelChildrenId: new Map<string, string[]>(),
            groupsLevelsIdToParentsLevelsId: new Map<string, Set<string>>(),
        };

        if (template == null) {
            return templateLevelsIdToLevelsChildrenId;
        }

        templateLevelsIdToLevelsChildrenId = this.getGroupsLevelsIdToLevelsChildrenId(
            template.group,
            new Set([`${template.group.name}${groupType}`]),
        );

        return templateLevelsIdToLevelsChildrenId;
    }

    private getGroupsLevelsIdToLevelsChildrenId(
        group: IAccountMappingTemplateGroup,
        upperParentIds: Set<string>,
    ): {
        groupsLevelIdToLevelChildrenId: Map<string, string[]>;
        groupsLevelsIdToParentsLevelsId: Map<string, Set<string>>;
    } {
        let itemsLevelsIdToLevelsChildrenId = new Map<string, string[]>();
        let groupsLevelIdToLevelChildrenId = new Map<string, string[]>();
        let uniqueGroupChildrenId: string[] = [];
        const innerGroupsChildrenId: string[] = [];
        const groupChildrenId: string[] = [];
        const parentsLevelsIds: string[] = [];
        const groupsLevelsIdToLevelChildrenId: Map<string, string[]>[] = [];
        const groupsLevelsIdsToParentsLevelsId: Map<string, Set<string>>[] = [];

        if (group != null) {
            if (group.items.length > 0) {
                const itemsLevelsIdRelations = this.getItemsLevelIdOFGroupToLevelChildrenId(group, upperParentIds);
                groupsLevelsIdsToParentsLevelsId.push(itemsLevelsIdRelations.levelIdToParentLevelsIds);
                itemsLevelsIdToLevelsChildrenId = itemsLevelsIdRelations.levelIdToLevelChildrenId;
                groupsLevelsIdToLevelChildrenId.push(itemsLevelsIdToLevelsChildrenId);
                parentsLevelsIds.push(`${group.name}${groupType}`);
                groupChildrenId.push(...itemsLevelsIdToLevelsChildrenId.get(`${group.name}${groupType}`));
            }

            if (group.groups != null && group.groups.length > 0) {
                for (const currentGroup of group.groups) {
                    const innerGroupsLevelsIdRelations = this.getGroupsLevelsIdToLevelsChildrenId(
                        currentGroup,
                        upperParentIds.add(`${group.name}${groupType}`),
                    );
                    const innerGroupsLevelsIdToLevelChildrenId =
                        innerGroupsLevelsIdRelations.groupsLevelIdToLevelChildrenId;
                    groupsLevelsIdsToParentsLevelsId.push(innerGroupsLevelsIdRelations.groupsLevelsIdToParentsLevelsId);
                    innerGroupsLevelsIdToLevelChildrenId.forEach((value, key) => {
                        innerGroupsChildrenId.push(key, ...value);
                    });
                    let uniqueInnerGroupsChildrenId = [...new Set(innerGroupsChildrenId)];
                    innerGroupsLevelsIdToLevelChildrenId.set(`${group.name}${groupType}`, uniqueInnerGroupsChildrenId);
                    groupChildrenId.push(...uniqueInnerGroupsChildrenId);
                    groupsLevelsIdToLevelChildrenId.push(innerGroupsLevelsIdToLevelChildrenId);
                }
            }
            uniqueGroupChildrenId = [...new Set(groupChildrenId)];
        }
        groupsLevelIdToLevelChildrenId = this.mergeArrayOfMaps(groupsLevelsIdToLevelChildrenId);
        const groupsLevelsIdToParentsLevelsId = this.mergeArrayOfMapWitSetOfStringValues(
            groupsLevelsIdsToParentsLevelsId,
        );

        // because if the group has items and groups then the innerGroups map will overwrite the items map so we combine the values
        groupsLevelIdToLevelChildrenId.set(`${group.name}${groupType}`, uniqueGroupChildrenId);
        return { groupsLevelIdToLevelChildrenId, groupsLevelsIdToParentsLevelsId };
    }

    private getItemsLevelIdOFGroupToLevelChildrenId(
        group: IAccountMappingTemplateGroup,
        upperParentsLevelsIds: Set<string>,
    ): { levelIdToLevelChildrenId: Map<string, string[]>; levelIdToParentLevelsIds: Map<string, Set<string>> } {
        const itemLevelId: string[] = [];
        let itemsMap = new Map<string, string[]>();
        let fliAndSubFlisLevelsIdToLevelChildrenId = new Map<string, string[]>();
        let levelIdToLevelChildrenId = new Map<string, string[]>();
        const itemsLevelsIdToLevelChildrenId: Map<string, string[]>[] = [];
        const itemsLevelsIdToParentsLevelsIds: Map<string, Set<string>>[] = [];
        const upperParentsLevelsIdsOfItems = new Set([...upperParentsLevelsIds, `${group.name}${groupType}`]);

        for (const item of group.items) {
            itemLevelId.push(item.name);
            if (item != null) {
                let fliAndSubFlisAndAccountsId: string[] = [];
                const itemLevelIdRelations = this.getItemLevelIdRelations(item, upperParentsLevelsIdsOfItems);

                itemsLevelsIdToParentsLevelsIds.push(itemLevelIdRelations.levelIdToParentLevelsIds);
                fliAndSubFlisLevelsIdToLevelChildrenId = itemLevelIdRelations.levelIdToLevelChildrenId;

                itemsLevelsIdToLevelChildrenId.push(fliAndSubFlisLevelsIdToLevelChildrenId);
                fliAndSubFlisLevelsIdToLevelChildrenId.forEach((value, key) => {
                    fliAndSubFlisAndAccountsId.push(key, ...value);
                });
                let itemLevelChildrenId = [...new Set(fliAndSubFlisAndAccountsId)];
                itemLevelId.push(...itemLevelChildrenId);
            }
        }
        itemsLevelsIdToParentsLevelsIds.push(
            new Map<string, Set<string>>([[`${group.name}${groupType}`, upperParentsLevelsIds]]),
        );
        itemsMap.set(`${group.name}${groupType}`, itemLevelId);
        itemsLevelsIdToLevelChildrenId.push(itemsMap);
        levelIdToLevelChildrenId = this.mergeArrayOfMaps(itemsLevelsIdToLevelChildrenId);
        const levelIdToParentLevelsIds = this.mergeArrayOfMapWitSetOfStringValues(itemsLevelsIdToParentsLevelsIds);
        return { levelIdToLevelChildrenId, levelIdToParentLevelsIds };
    }

    private getItemLevelIdRelations(
        item: AccountMappingTemplateGroupItem,
        upperParentsLevelsIds: Set<string>,
    ): { levelIdToLevelChildrenId: Map<string, string[]>; levelIdToParentLevelsIds: Map<string, Set<string>> } {
        if (item.type !== ItemType.SUB_TOTAL && item.type !== ItemType.EMPTY && item.type !== ItemType.HEADER) {
            return this.getFliLevelIdsToLevelChildrenId(
                item as IAccountMappingTemplateGroupItemFLI,
                upperParentsLevelsIds,
            );
        } else {
            return {
                levelIdToLevelChildrenId: new Map<string, string[]>([[`${item.name}${groupType}`, []]]),
                levelIdToParentLevelsIds: new Map<string, Set<string>>([
                    [`${item.name}${groupType}`, upperParentsLevelsIds],
                ]),
            };
        }
    }

    private getFliLevelIdsToLevelChildrenId(
        item: IAccountMappingTemplateGroupItemFLI,
        upperParentsLevelsIds: Set<string>,
    ): { levelIdToLevelChildrenId: Map<string, string[]>; levelIdToParentLevelsIds: Map<string, Set<string>> } {
        const fliAccountsId: string[] = [];
        const fliReclassesAccountsId: string[] = [];
        const fliLevelIdToLevelChildrenId = new Map<string, string[]>();
        const subFliLevelIdToAccountsId = new Map<string, string[]>();
        let levelIdToLevelChildrenId = new Map<string, string[]>();
        const levelIdToParentLevelsIds = new Map<string, Set<string>>();
        const accountsAndSubFlisIdsOfFli: string[] = [];

        for (const account of item.accounts) {
            if (
                account.type !== ItemType.SUB_FLI &&
                account.type !== ItemType.EMPTY &&
                account.type !== ItemType.HEADER &&
                account.type !== ItemType.OF_THAT_ITEM
            ) {
                fliAccountsId.push((account as IAccount).accountNumber);
            } else if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                const subFliParentsIds = new Set<string>([...upperParentsLevelsIds, `${item.name}${itemType}`]);
                levelIdToParentLevelsIds.set(`${account.name}${itemType}`, subFliParentsIds);
                subFliLevelIdToAccountsId.set(`${account.name}${itemType}`, this.getAccountsOfSubfli(account));
            }
        }

        for (const reclassesAccount of item.reclassesAccounts) {
            fliReclassesAccountsId.push(reclassesAccount.accountNumber);
        }

        subFliLevelIdToAccountsId.forEach((value, key) => {
            accountsAndSubFlisIdsOfFli.push(key, ...value);
        });

        accountsAndSubFlisIdsOfFli.push(...fliAccountsId, ...fliReclassesAccountsId);
        levelIdToParentLevelsIds.set(`${item.name}${itemType}`, upperParentsLevelsIds);
        fliLevelIdToLevelChildrenId.set(`${item.name}${itemType}`, accountsAndSubFlisIdsOfFli);

        levelIdToLevelChildrenId = this.mergeTwoMaps(fliLevelIdToLevelChildrenId, subFliLevelIdToAccountsId);
        return { levelIdToLevelChildrenId, levelIdToParentLevelsIds };
    }

    private getAccountsOfSubfli(subFli: IAccountMappingSubFLI): string[] {
        const accountsOfSubFli: string[] = [];
        for (const account of subFli.accounts) {
            accountsOfSubFli.push(account.accountNumber);
        }
        for (const reclassesAccount of subFli.reclassesAccounts) {
            accountsOfSubFli.push(reclassesAccount.accountNumber);
        }
        return accountsOfSubFli;
    }

    // this method overrides the values of the second map with these of the first one in case of duplicate keys, so you have to pass the main map as the first parameter.
    private mergeTwoMaps(mainMap: Map<string, string[]>, map2: Map<string, string[]>): Map<string, string[]> {
        const mergedMap = new Map<string, string[]>();
        mainMap.forEach((value, key) => {
            map2.has(key) ? mergedMap.set(key, union<string>(value, map2.get(key))) : mergedMap.set(key, value);
        });
        map2.forEach((value, key) => {
            if (!mergedMap.has(key)) {
                mergedMap.set(key, value);
            }
        });

        return mergedMap;
    }

    private mergeArrayOfMaps(arrayOfMaps: Map<string, string[]>[]): Map<string, string[]> {
        const spreadMaps = arrayOfMaps.map((value) => [...value]);
        const flattenedMaps = flatten<[string, string[]]>(spreadMaps);
        return new Map<string, string[]>(flattenedMaps);
    }

    private mergeArrayOfMapWitSetOfStringValues(arrayOfMaps: Map<string, Set<string>>[]): Map<string, Set<string>> {
        const spreadMaps = arrayOfMaps.map((value) => [...value]);
        const flattenedMaps = flatten<[string, Set<string>]>(spreadMaps);
        return new Map<string, Set<string>>(flattenedMaps);
    }
}
